import { CellWithEllipsis } from '@components/DataTable/CellWithEllipsis';
import { CurrencyCell } from '@components/DataTable/CurrencyCell';
import { DetailLink } from '@components/DetailLink';
import { EllipsisText } from '@components/EllipsisText';
import { Rut } from '@components/Rut';
import { type Operation } from '@operations/domain/operation/types';
import { OPERATIONS_PATHS } from '@operations/pages/paths';
import { getQuoteRequestStatusLabel } from '@store/skeletor/quoteRequest/states/get/getQuoteRequestStatusLabel';
import { type QuoteRequestStatus } from '@store/skeletor/quoteRequest/states/types';
import { type ColumnDef } from '@tanstack/react-table';
import { safeDateFormat } from '@utils/columns/safeDateFormat';

import { OperationDecisionBadges } from '../OperationDecisionBadges';

const DATE_FORMAT = 'dd/MM/yyyy HH:mm';

const columns: ColumnDef<Operation>[] = [
  {
    id: 'clientName',
    accessorKey: 'clientName',
    header: 'Cliente',
    cell: ({ row }) => {
      const { id, clientName, clientRut } = row.original;

      return (
        <div>
          <DetailLink
            to={OPERATIONS_PATHS.DETAIL_HOME(id)}
            id={`link-operation-details-${id}`}
          >
            <EllipsisText>{clientName}</EllipsisText>
          </DetailLink>
          <Rut fullRut={clientRut} />
        </div>
      );
    },
    size: 300,
  },
  {
    id: 'guideNumber',
    accessorKey: 'guideNumber',
    header: 'N° Guía',
    size: 80,
    cell: ({ getValue }) => getValue() ?? '-',
    enableSorting: false,
  },
  {
    id: 'operationNumber',
    accessorKey: 'operationNumber',
    header: 'N° Operación',
    size: 110,
    cell: ({ getValue }) => getValue() ?? '-',
    enableSorting: false,
  },
  {
    id: 'executiveEmail',
    accessorKey: 'executiveEmail',
    header: 'Ejecutivo',
    cell: CellWithEllipsis,
    size: 200,
  },
  {
    id: 'currentState',
    accessorKey: 'currentStateName',
    header: 'Estado',
    cell: ({ getValue }) => {
      return (
        <EllipsisText>
          {getQuoteRequestStatusLabel(getValue() as QuoteRequestStatus)}
        </EllipsisText>
      );
    },
    size: 100,
    enableSorting: true,
  },
  {
    id: 'decisions',
    accessorKey: 'decisions',
    header: 'Aprobaciones',
    cell: ({ row }) => {
      const { decisions, currentStateName } = row.original;

      return (
        <OperationDecisionBadges
          currentStateName={currentStateName}
          decisions={decisions}
        />
      );
    },
    enableSorting: false,
  },
  {
    id: 'requestedAt',
    accessorKey: 'requestedAt',
    header: 'Fecha de creación',
    cell: ({ row }) => {
      const { createdAt } = row.original;

      return safeDateFormat(createdAt, DATE_FORMAT);
    },
    size: 140,
  },
  {
    id: 'totalAmount',
    accessorKey: 'totalAmount',
    header: 'Monto total',
    cell: CurrencyCell,
    meta: {
      className: 'text-nowrap tabular-nums text-end',
      headClassName: '*:float-end',
    },
    size: 180,
  },
];

export { columns };
